import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Survey.css"; // or another CSS file if you have one for styling

function ResultsPage() {
  const navigate = useNavigate();

  /************************************************
   * Result #1
   ************************************************/
  const analysisOne = {
    surveyTitle: "Engineering Firm - Employee Feedback",
    responseCount: 22,
    bulletPoints: [
      "Supply chain delays and coordination issues with architectural teams are major challenges across different roles and projects.",
      "Collaboration, design reviews, site visits, and coordination meetings have been instrumental in addressing challenges and ensuring progress on projects.",
      "The use of VR and 3D scanning tools for digital design collaboration is a prominent trend, with many finding it helpful in overcoming challenges.",
      "Despite the pressures and challenges of tight deadlines and last-minute design changes, team members have been displaying resilience and adaptability in finding solutions and keeping the project on track.",
      "Working in hybrid work schedules and using creative solutions to compensate for resource constraints in specialized software and other areas have been common experiences.",
    ],
    actionableInsights: [
      "Further enhance the use of VR, 3D scanning tools, and other digital collaboration tools to help manage design changes, ensure code compliance, and overcome other project challenges.",
      "Consider investing more resources into dealing with supply chain management, to mitigate the impact of delays on different projects and roles.",
      "Improve architectural team coordination to avoid causing stress among the project team members.",
      "Building resilience and problem-solving capabilities through regular training can help teams better handle unexpected site conditions and resource constraints.",
      "Ensure frequent design reviews, onsite visits, and coordination meetings to keep everyone on the same page and swiftly address potential clashes or other issues.",
    ],
    summaryText:
      "Based on the survey, it is evident that supply chain delays and coordination issues with architectural teams pose significant challenges across various roles and projects. Technological aids such as VR and 3D scanning tools have played a crucial role in digital design collaboration, enhancing the ability of teams to overcome challenges. Despite the pressures of tight deadlines and last-minute design changes, the participants exhibited resilience and adaptability, applying innovative solutions to keep projects on track. Mixed experiences of reward and exhaustion were reported while working in hybrid work schedules and contending with resource constraints on specialized software. Collaboration, frequent design reviews, site visits, and coordination meetings have proved crucial in addressing challenges and ensuring project progress. Overall, the experience underscores the value of collaboration and adaptability in the face of unexpected project challenges.",
    topWords: [
      { word: "project", count: 49 },
      { word: "design", count: 37 },
      { word: "challenges", count: 27 },
      { word: "coordination", count: 26 },
      { word: "deadlines", count: 25 },
      { word: "resilience", count: 24 },
      { word: "hybrid", count: 20 },
      { word: "collaboration", count: 18 },
      { word: "vr", count: 10 },
      { word: "scanning", count: 8 },
    ],
  };

  /************************************************
   * Result #2
   ************************************************/
  const analysisTwo = {
    surveyTitle: "Workplace Review",
    responseCount: 74,
    bulletPoints: [
      "Many respondents experienced a mix of positive and negative experiences in their respective roles, regardless of whether their role was remote, hybrid, or in-office.",
      "Effective collaboration and communication within teams, fostered by Agile processes and supported by digital tools like Slack and Zoom, were instrumental to successfully overcoming project challenges.",
      "Unclear product requirements and communication gaps in hybrid setups caused some difficulties, including rushed tasks, stress, and burnout.",
    ],
    actionableInsights: [
      "Improving project requirement clarity could reduce anxiety and aid in task completion, enhancing team efficiency and job satisfaction.",
      "Enhancing communication systems and tools could minimize communication gaps in hybrid setups, improving productivity.",
      "Processes for managing workload could be analyzed and adapted to minimize team burnout and optimize task management.",
    ],
    summaryText:
      "The survey received an array of responses from individuals in various roles within software development teams, including Product Managers, UX/UI Designers, Scrum Masters, and DevOps Engineers. Overall, the sentiment across the board was a mixture of both rewarding and challenging experiences. Agile processes were found particularly useful for teams, allowing them to adapt quickly to changes and make efficient decisions. Regular meetings and code reviews were also highlighted as a significant aid in keeping teams unified towards common goals. However, some issues were noticed hindering the fluid operation of tasks. Unclear product requirements, the stress from tight deadlines, and communication gaps in hybrid work setups were some frequently cited difficulties. Despite these, teams were successful in overcoming the hurdles, showcasing resilience and effective problem-solving. They relied heavily on communication tools like Slack and Zoom for staying connected and informed. Several respondents expressed their optimism, pride, and appreciation for the team's achievements, all while noting the critical importance of teamwork, Agile processes, and the role of effective communication tools within the successful delivery of high-quality software.",
    topWords: [
      { word: "team", count: 175 },
      { word: "communication", count: 87 },
      { word: "agile", count: 73 },
      { word: "reviews", count: 72 },
      { word: "challenges", count: 70 },
      { word: "hybrid", count: 68 },
      { word: "deadlines", count: 64 },
      { word: "zoom", count: 44 },
      { word: "slack", count: 38 },
      { word: "burnout", count: 25 },
    ],
  };

  /************************************************
   * Result #3
   ************************************************/
  const analysisThree = {
    surveyTitle: "Group Survey",
    responseCount: 289,
    bulletPoints: [
      "Employees work in diverse roles within the tech corporation, reflecting a wide range of experiences and challenges.",
      "Collaboration, facilitated by Agile processes, code reviews, and communication tools, like Teams and Asana, is highly valued and seen as instrumental in overcoming challenges.",
      "Common challenges include dealing with overlapping product reviews, urgent bug fixes, stringent code-freeze deadlines, internal re-org changes, security compliance audits, and global data center outages.",
      "Despite the dynamic and demanding nature of their jobs, employees express satisfaction, pride, and a sense of fulfillment derived from their roles.",
      "Key projects include AI-based analytics pipelines, mobile SDKs, and global payments systems, which maintain the cutting-edge status of the corporation.",
    ],
    actionableInsights: [
      "More clarity in cross-team goal-setting could enhance productivity and job satisfaction among employees.",
      "Processes to manage workload better and avoid burnout could improve employee well-being.",
      "Efficient techniques for managing overlapping product reviews should be implemented.",
      "Further training and readiness programs around handling global data center outages could improve the organization's crisis management.",
      "The corporation should continue to value and promote a culture of innovation, resilience, adaptability, and teamwork to navigate challenges effectively.",
    ],
    summaryText:
      "The survey summaries provide insights into employee experiences within a tech corporation occupying roles like Product Marketing Manager, UX Researcher, Mobile Applications Developer, and Technical Program Manager. The strong emphasis is on the importance of collaboration, Agile practices, regular code reviews, and utilization of communication tools like Teams and Asana to navigate the inherently dynamic and demanding nature of their roles. Despite facing several challenges like overlapping product reviews, code-freeze deadlines, and global data center outages, the sentiment remains positive—characterized by a sense of pride, fulfillment, and motivation derived from solving these complex problems. Successful projects include AI-based analytics pipelines, mobile SDKs, and global payment systems, thereby ensuring the corporation stays at the cutting edge. However, areas needing attention include providing more clarity in cross-team goal-setting, managing workloads to avoid employee burnout, and preparing better for crises such as global data center outages. Overall, promoting a culture of collaboration and innovation can continue to inspire employees to deliver high-quality results.",
    topWords: [
      { word: "team", count: 380 },
      { word: "reviews", count: 328 },
      { word: "code", count: 306 },
      { word: "collaboration", count: 305 },
      { word: "processes", count: 301 },
      { word: "communication", count: 300 },
      { word: "agile", count: 289 },
      { word: "reorg", count: 280 },
      { word: "sdk", count: 265 },
      { word: "fulfillment", count: 200 },
    ],
  };

  /************************************************
   * Result #4
   ************************************************/
  const analysisFour = {
    surveyTitle: "Health System Group Survey",
    responseCount: 562,
    bulletPoints: [
      "Common challenges across healthcare roles include strict insurance regulations, high turnover rates, staffing shortages, scheduling conflicts, and technological difficulties in implementing telehealth services and EHR systems.",
      "Despite the challenges, healthcare professionals across different roles have expressed overall positive trends, resilience, and a high level of dedication to improving patient outcomes.",
      "Telehealth services have been widely ingeminated with a significant impact toward improving accessibility and patient well-being, despite a few hurdles.",
      "Collaboration among healthcare teams plays a critical role in navigating difficulties, epitomizing the effectiveness of care coordination meetings and follow-ups for quality patient care.",
      "Implementation of remote patient monitoring tools and transitioning to new EHR systems are enhancing patient tracking and making the work of healthcare professionals more efficient.",
    ],
    actionableInsights: [
      "Efforts should be focused on mitigating high turnover rates and staffing shortages to maintain the continuity and quality of patient care.",
      "Resources should be invested in alleviating scheduling conflicts and technology-related difficulties commonly encountered in telehealth services by means of support systems and training.",
      "Healthcare professionals would benefit from clearer guidelines on treatment protocols and assistance in navigating the complex landscape of insurance regulations.",
      "Investments in the development of healthcare information technology, including EHR systems and remote patient monitoring tools, could improve patient outcomes and streamline work processes.",
      "Fostering an environment that encourages effective collaboration and teamwork will be advantageous in overcoming prevalent challenges in the healthcare sector.",
    ],
    summaryText:
      "The survey data reveals the experiences of diverse healthcare professionals including nutritionists, mental health counsellors, hospital administrators, physical therapists, and others. Despite facing challenges such as high staff turnover rates, strict insurance regulations, unclear treatment protocols, and technological difficulties in ingeminating telehealth services and EHR systems, these professionals have demonstrated resilience and dedication in their roles. Positive trends have been reported, common among which are improved patient outcomes, effective teamwork and collaboration, successful telehealth implementation, and efficient use of EHR systems and remote patient tracking tools. However, the data also suggest the need for actions that address staff retention, clarify treatment protocols, assist with insurance-related difficulties, and provide technological support for telehealth and EHR systems. Promoting collaboration and effective team communication likewise stand as essential strategies for navigating the complex landscape of healthcare.",
    topWords: [
      { word: "patient", count: 1588 },
      { word: "care", count: 1213 },
      { word: "team", count: 1062 },
      { word: "health", count: 686 },
      { word: "challenges", count: 685 },
      { word: "insurance", count: 622 },
      { word: "professionals", count: 600 },
      { word: "telehealth", count: 598 },
      { word: "ehr", count: 520 },
      { word: "collaboration", count: 410 },
    ],
  };

  /************************************************
   * Result #5
   ************************************************/
  const analysisFive = {
    surveyTitle: "Business Survey - Outlook on AI",
    responseCount: 992,
    bulletPoints: [
      "Excitement and optimism surround the transformative potential of AI across various industries, particularly pertaining to improving efficiency, precision, and customer experience.",
      "Concerns related to infrastructure limitations, intricacies of data strategy, potential job displacement, high implementation costs, and ethical implications (data privacy, AI bias) are widespread.",
      "Lack of specialized AI talent is a recurrent concern, hindering efficient utilization of AI capabilities.",
      "Despite concerns and hurdles, successful AI implementations in areas like predictive modeling, customer service, automation, and data analysis have bolstered confidence in AI’s potential.",
      "Investments in training and upskilling initiatives are ramping up as organizations prepare for AI integration, addressing potential job displacement fears and managing algorithmic bias.",
      "There is anticipation for regulations governing AI, in light of fears of AI surpassing legal and ethical frameworks.",
      "AI is viewed as a tool to augment and enhance human capabilities rather than replace it, acknowledging the need for a delicate balance between effective AI utilization and human expertise.",
    ],
    actionableInsights: [
      "Invest in robust training, upskilling programs, and continuous learning initiatives to prepare the workforce for AI adoption and assuage fears around job displacement.",
      "Develop a clear, comprehensive data strategy and ensure data governance and quality for seamless AI integration.",
      "Address infrastructure limitations via partnerships with AI vendors or cloud-based solutions for AI adoption.",
      "Maintain transparency about AI’s impact on job roles and processes to minimize job insecurity and fears.",
      "Address ethical implications such as data privacy and unbiased AI algorithms by creating clear guidelines and regulatory frameworks for responsible AI use.",
      "Keep abreast of AI regulations—rapid changes in the regulatory environment require vigilance to remain compliant.",
      "Tackle the AI talent shortage by forging partnerships with universities or research institutions and collaborating with AI experts or tech companies.",
    ],
    summaryText:
      "Opinions of AI’s impact on industries vary, with a mixture of cautious optimism, skepticism, enthusiasm, and apprehension among professionals. AI’s potential for enhancing operational efficiency, aiding decision-making, and improving customer service is recognized. However, concerns over potential job displacement, high costs, ethical implications (like data privacy and AI bias), and the shortage of specialized AI talent overshadow these positives. In response, organizations focus on comprehensive data strategies, training and upskilling initiatives, and forging collaborations with AI experts and tech companies. Successful use cases in data analysis, automation, and predictive modeling further fuel optimism in AI’s transformative power. While AI stands poised to revolutionize business operations, ethical guidelines, transparency, accountability, and robust regulations remain crucial. Striking a balance between AI utilization and human expertise is seen as the key to harnessing AI effectively without undermining workforce stability or ethical standards.",
    topWords: [
      { word: "ai", count: 7656 },
      { word: "potential", count: 2417 },
      { word: "data", count: 1251 },
      { word: "operations", count: 1155 },
      { word: "customers", count: 920 },
      { word: "training", count: 879 },
      { word: "workforce", count: 602 },
      { word: "bias", count: 580 },
      { word: "adoption", count: 490 },
      { word: "experts", count: 412 },
    ],
  };

  /************************************************
   * React State for Expand/Collapse
   ************************************************/
  const [expanded, setExpanded] = useState({
    analysis5: true,  // default expanded
    analysis4: false, // default collapsed
    analysis3: false,
    analysis2: false,
    analysis1: false,
  });

  // Toggles the expanded state for a particular analysis
  const toggleExpand = (analysisKey) => {
    setExpanded((prev) => ({
      ...prev,
      [analysisKey]: !prev[analysisKey],
    }));
  };

  /************************************************
   * Reusable AnalysisBlock
   ************************************************/
  const AnalysisBlock = ({ data, isExpanded, onToggle, analysisKey }) => {
    const {
      surveyTitle,
      responseCount,
      bulletPoints,
      actionableInsights,
      summaryText,
      topWords,
    } = data;

    return (
      <div className="survey-container">
        {/* Header with Expand/Collapse button */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h1 className="analysis-title" style={{ margin: 0 }}>AI Analysis</h1>

          {/* Same style as "Register Now" button */}
          <button
            className="logout-btn"
            style={{ marginLeft: "auto" }}
            onClick={onToggle}
          >
            {isExpanded ? "Collapse" : "Expand"}
          </button>
        </div>

        {/* Number of responses always visible */}
        <p style={{ marginTop: "0.5rem" }}>Number of Responses: {responseCount}</p>

        {isExpanded && (
          <>
            {/* Survey Title */}
            <h4>Survey Title: {surveyTitle}</h4>

            {/* Key Bullet Points */}
            <section style={{ marginTop: "1rem" }}>
              <h3>Key Bullet Points</h3>
              <ul>
                {bulletPoints.map((bp, idx) => (
                  <li key={idx}>{bp}</li>
                ))}
              </ul>
            </section>

            {/* Actionable Insights */}
            <section style={{ marginTop: "1rem" }}>
              <h3>Actionable Insights</h3>
              <ul>
                {actionableInsights.map((insight, idx) => (
                  <li key={idx}>{insight}</li>
                ))}
              </ul>
            </section>

            {/* Overall Summary */}
            <section style={{ marginTop: "1rem" }}>
              <h3>Overall Summary</h3>
              <p>{summaryText}</p>
            </section>

            {/* Top Words */}
            <section style={{ marginTop: "1rem" }}>
              <h3>Top Words</h3>
              {topWords
                .sort((a, b) => b.count - a.count)
                .map((w, idx) => (
                  <div key={idx}>
                    {w.word}: {w.count}
                  </div>
                ))}
            </section>
          </>
        )}
      </div>
    );
  };

  /************************************************
   * FINAL RENDER
   ************************************************/
  return (
    <div>
      {/* Analysis #5 (Top, default expanded) */}
      <AnalysisBlock
        data={analysisFive}
        isExpanded={expanded.analysis5}
        onToggle={() => toggleExpand("analysis5")}
        analysisKey="analysis5"
      />

      {/* Analysis #4 */}
      <div style={{ marginTop: "2rem" }}>
        <AnalysisBlock
          data={analysisFour}
          isExpanded={expanded.analysis4}
          onToggle={() => toggleExpand("analysis4")}
          analysisKey="analysis4"
        />
      </div>

      {/* Analysis #3 */}
      <div style={{ marginTop: "2rem" }}>
        <AnalysisBlock
          data={analysisThree}
          isExpanded={expanded.analysis3}
          onToggle={() => toggleExpand("analysis3")}
          analysisKey="analysis3"
        />
      </div>

      {/* Analysis #2 */}
      <div style={{ marginTop: "2rem" }}>
        <AnalysisBlock
          data={analysisTwo}
          isExpanded={expanded.analysis2}
          onToggle={() => toggleExpand("analysis2")}
          analysisKey="analysis2"
        />
      </div>

      {/* Analysis #1 */}
      <div style={{ marginTop: "2rem" }}>
        <AnalysisBlock
          data={analysisOne}
          isExpanded={expanded.analysis1}
          onToggle={() => toggleExpand("analysis1")}
          analysisKey="analysis1"
        />
      </div>

      {/* Buttons at the bottom */}
      <div style={{ marginTop: "2rem", textAlign: "center" }}>
        <button
          className="logout-btn"
          onClick={() => navigate("/register")}
          style={{ marginRight: "1rem" }}
        >
          Register Now
        </button>

        {/* New "Try It" button */}
        <button
          className="logout-btn"
          onClick={() => navigate("/try-it")}
        >
          Try It
        </button>
      </div>
    </div>
  );
}

export default ResultsPage;
